import { FC, useCallback, useMemo, useState } from "react";

import { Center, ErrorWithRetry, Spinner, Text } from "@/components/ui";
import API from "@/hooks/api";

import MidtermsTracker from "./MidtermsTracker";
import { DEFAULT_SETTINGS } from "./model";
import { loadClientWeight, saveClientWeight, unpackSample } from "./storage";
import type {
  ModelSettings,
  Snapshot,
  SnapshotSamples,
  SnapshotSummariesByWeight,
} from "./types";

/* ── Subcomponents ─────────────────────────────── */

/** The published tracker: Foresight only, and the own-vote weight is the
    one setting a client tunes. */
const PublishedTracker: FC<{ snapshot: Snapshot }> = ({ snapshot }) => {
  const [ownWeight, setOwnWeight] = useState(loadClientWeight);
  const settings = useMemo(
    () => ({ ...DEFAULT_SETTINGS, ownWeight }),
    [ownWeight]
  );
  const samples = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(snapshot.samples).map(([state, sample]) => [
          state,
          unpackSample(sample),
        ])
      ),
    [snapshot.samples]
  );

  const updateSettings = useCallback((next: ModelSettings) => {
    setOwnWeight(next.ownWeight);
    saveClientWeight(next.ownWeight);
  }, []);

  return (
    <MidtermsTracker
      mode={{
        publishedAt: snapshot.published_at,
        samples,
        summaries: snapshot.summaries,
        variant: "client",
      }}
      onSettingsChange={updateSettings}
      settings={settings}
    />
  );
};

/* ── Component ─────────────────────────────────── */

/** Client page body: the last published snapshot, loaded once. */
const ClientTracker: FC = () => {
  const { error, loading, refresh, snapshot, unavailable } =
    API.Midterms.useGetSnapshot<SnapshotSamples, SnapshotSummariesByWeight>();

  if (loading) {
    return (
      <Center className="flex-1">
        <Spinner color="rgb(var(--color-primary-500))" />
      </Center>
    );
  }
  if (unavailable) {
    return (
      <Center className="flex-1">
        <Text className="text-sm text-typography-500">
          {unavailable === "forbidden"
            ? "You don't have access to this page."
            : "Nothing has been published yet."}
        </Text>
      </Center>
    );
  }
  if (!snapshot) {
    return (
      <ErrorWithRetry
        error={error ?? "The tracker couldn't be loaded."}
        onRetry={refresh}
      />
    );
  }
  return <PublishedTracker snapshot={snapshot} />;
};

export default ClientTracker;
